import { Shell } from '../components/Shell';

type Plan = {
  key: string;
  name: string;
  price: string;
  cadence: string;
  blurb: string;
  features: { label: string; locked?: boolean }[];
  cta: string;
  ctaHref: string;
  featured?: boolean;
};

const PLANS: Plan[] = [
  {
    key: 'free',
    name: 'Free',
    price: '€0',
    cadence: 'forever',
    blurb: 'One site, no credit card. See if the WeDPO CMP fits before paying anything.',
    features: [
      { label: '1 domain' },
      { label: 'Unlimited consent receipts' },
      { label: 'EDPB-compliant defaults' },
      { label: 'Monthly cookie re-scan' },
      { label: 'Auto-generated cookie policy text' },
      { label: '"Powered by WeDPO" attribution required', locked: true },
      { label: 'No custom branding', locked: true },
    ],
    cta: 'Current plan',
    ctaHref: '#',
  },
  {
    key: 'solo',
    name: 'Solo',
    price: '€19',
    cadence: 'per month',
    blurb: 'For founders, indie sites, and small teams who want it to look like their brand.',
    features: [
      { label: '5 domains' },
      { label: 'White-label (no WeDPO attribution)' },
      { label: 'Custom logo, color, theme' },
      { label: 'Weekly re-scan + alerts' },
      { label: 'Consent log CSV export' },
      { label: 'Email support' },
    ],
    cta: 'Choose Solo',
    ctaHref: '/checkout?plan=solo',
    featured: true,
  },
  {
    key: 'agency',
    name: 'Agency',
    price: '€79',
    cadence: 'per month',
    blurb: 'For consultants and agencies managing multiple clients in one workspace.',
    features: [
      { label: '25 domains' },
      { label: '5 team seats' },
      { label: 'Multi-client workspaces' },
      { label: 'White-label PDF reports' },
      { label: 'ROPA draft generation' },
      { label: 'Daily re-scan + alerts' },
      { label: 'Priority support' },
    ],
    cta: 'Choose Agency',
    ctaHref: '/checkout?plan=agency',
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    cadence: 'annual',
    blurb: 'For groups, regulated industries, and in-house legal teams that need bespoke terms.',
    features: [
      { label: 'Unlimited domains' },
      { label: 'SSO & multi-region' },
      { label: 'Dedicated DPO support' },
      { label: 'Custom SLA + DPA' },
      { label: 'API access' },
      { label: 'Audit log export' },
    ],
    cta: 'Talk to us',
    ctaHref: 'mailto:sales@wedpo.com?subject=Enterprise%20enquiry',
  },
];

export function PricingPage() {
  return (
    <Shell centerLabel="Pricing">
      <section className="border-b" style={{ borderColor: 'var(--rule)' }}>
        <div className="max-w-[1200px] mx-auto px-6 md:px-14 py-14">
          <span className="pill mb-3 inline-flex">
            <span className="pill-dot" /> 06 · Pricing · pick a plan that fits
          </span>
          <h1 className="font-medium mt-4 mb-3" style={{ fontSize: 'clamp(28px, 3.4vw, 44px)', lineHeight: 1.04, letterSpacing: '-0.03em' }}>
            Simple pricing. <span className="grad-text">No surprises.</span>
          </h1>
          <p className="text-[15px] leading-[1.55] mb-10 max-w-[640px]" style={{ color: 'rgba(13,11,31,0.7)' }}>
            Start free, upgrade when you outgrow it. All plans include the lawyer-reviewed compliance engine and unlimited consent receipts.
          </p>

          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}
          >
            {PLANS.map((p) => (
              <div
                key={p.key}
                style={{
                  background: p.featured ? 'var(--ink)' : 'white',
                  color: p.featured ? 'var(--paper)' : 'var(--ink)',
                  border: p.featured ? '1px solid var(--ink)' : '1px solid var(--rule)',
                  borderRadius: 6,
                  padding: '26px 24px 24px',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                }}
              >
                {p.featured && (
                  <span
                    style={{
                      position: 'absolute',
                      top: -10,
                      left: 24,
                      background: 'var(--grad)',
                      color: 'white',
                      padding: '4px 10px',
                      borderRadius: 99,
                      fontFamily: 'IBM Plex Mono, monospace',
                      fontSize: 9,
                      letterSpacing: '1.4px',
                      textTransform: 'uppercase',
                      fontWeight: 500,
                    }}
                  >
                    Most popular
                  </span>
                )}
                <span className="meta" style={{ display: 'block', marginBottom: 8, color: p.featured ? 'rgba(240,238,233,0.55)' : undefined }}>
                  {p.name}
                </span>
                <div style={{ fontSize: 38, fontWeight: 500, letterSpacing: '-0.035em', lineHeight: 1, marginBottom: 10 }}>
                  {p.price}{' '}
                  <small style={{ fontSize: 13, fontWeight: 400, color: p.featured ? 'rgba(240,238,233,0.55)' : 'var(--muted-ink)', marginLeft: 4, letterSpacing: 0 }}>
                    {p.cadence}
                  </small>
                </div>
                <p style={{ fontSize: 13, lineHeight: 1.55, color: p.featured ? 'rgba(240,238,233,0.75)' : 'rgba(13,11,31,0.7)', margin: '0 0 18px' }}>
                  {p.blurb}
                </p>
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 22px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  {p.features.map((f) => (
                    <li key={f.label} style={{ fontSize: 13, lineHeight: 1.45, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      {f.locked ? (
                        <span style={{ color: 'var(--flag)', flexShrink: 0, fontWeight: 500, marginTop: 1 }}>×</span>
                      ) : (
                        <span style={{ color: p.featured ? 'var(--grad-3)' : 'var(--live)', flexShrink: 0, fontWeight: 500, marginTop: 1 }}>✓</span>
                      )}
                      <span style={{ color: f.locked ? 'var(--muted-ink)' : 'inherit' }}>{f.label}</span>
                    </li>
                  ))}
                </ul>
                {p.key === 'free' ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '12px 16px',
                      fontSize: 13,
                      background: 'var(--paper-2)',
                      color: 'var(--muted-ink)',
                      borderRadius: 'var(--radius)',
                    }}
                  >
                    {p.cta}
                  </div>
                ) : (
                  <a
                    href={p.ctaHref}
                    className="btn"
                    style={{
                      width: '100%',
                      justifyContent: 'center',
                      background: p.featured ? 'var(--paper)' : 'var(--ink)',
                      color: p.featured ? 'var(--ink)' : 'var(--paper)',
                    }}
                  >
                    {p.cta}
                  </a>
                )}
              </div>
            ))}
          </div>

          <p style={{ marginTop: 32, textAlign: 'center', fontSize: 12, color: 'var(--muted-ink)', lineHeight: 1.55 }}>
            All prices in EUR, exclusive of VAT (calculated at checkout). Cancel any time.{' '}
            <a href="https://scanner.wedpo.com/refund-and-cancellation" className="underline" style={{ color: 'var(--ink)' }}>
              Refund &amp; cancellation policy
            </a>
            .
          </p>
        </div>
      </section>
    </Shell>
  );
}
